import React, { Component } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import DealerSignupService from '../../../services/dealerSignupService';
import BookmarkThisPage from '../../partial/BookmarkThisPage';
import { CURRENT_ASSIST_TERMS_VERSION, ASSIST_AGREEMENT_URL } from '../../../constants/assistTermsVersion';
import Logo from '../../../assets/Jobvious_logo.png';
import '../../../assets/css/dealerSignup.css';

class DealerSignup extends Component {
  constructor(props) {
    super(props);

    this.state = {
      // Form fields
      businessName: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',

      // Attribution from URL params
      attributionSource: 'direct', // direct, internal, installer, assist
      referringInstallerId: null,
      referralCode: null,
      installerCategories: 'Paint', // Default to Paint, or use installer's categories
      // Set once, at signup. A dealership is strictly automotive OR marine.
      assetType: 'auto',

      // Assist program agreement (only used when attributionSource === 'assist')
      assistTermsAccepted: false,

      // UI state
      loading: false,
      submitted: false,
      error: null,
      errorCode: null,
      errorAction: null,
      successData: null,
      validatingReferral: false,
      referralValid: null,
      referrerName: '',

      // Google Places
      autocomplete: null,
      placeDetails: null,
      // Identity + coordinates straight from the selected place. placeId is a stable
      // key for the physical business (dedupes two people signing up the same
      // dealership); lat/lng save the server a geocode round trip and remove the
      // GEOCODING_FAILED path. All null when the place was never selected.
      placeId: null,
      latitude: null,
      longitude: null
    };

    this.autocompleteInput = React.createRef();
  }

  componentDidMount() {
    // Parse URL parameters for attribution
    this.parseUrlParameters();

    // Initialize Google Maps Autocomplete
    this.initializeAutocomplete();
  }

  parseUrlParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    const source = urlParams.get('source');

    if (source === 'assist') {
      // Assist program signup — preferred installer assigned server-side
      // by proximity, so any ref param is ignored
      this.setState({ attributionSource: 'assist' });
    } else if (ref) {
      // Installer referral code
      this.setState({
        referralCode: ref,
        attributionSource: 'installer',
        validatingReferral: true
      }, () => {
        this.validateReferralCode(ref);
      });
    } else if (source === 'internal') {
      this.setState({ attributionSource: 'internal' });
    } else {
      this.setState({ attributionSource: 'direct' });
    }
  };

  validateReferralCode = async (code) => {
    try {
      const result = await DealerSignupService.validateInstallerReferral(code);

      if (result.valid) {
        // Get installer's categories to configure initial services
        const categoryData = await DealerSignupService.getInstallerCategories(result.installerId);

        this.setState({
          referralValid: true,
          referringInstallerId: result.installerId,
          referrerName: result.installerName,
          installerCategories: categoryData.installerCategories || 'Paint',
          validatingReferral: false
        });
      } else {
        this.setState({
          referralValid: false,
          validatingReferral: false,
          error: 'Invalid referral code. You can still sign up, but without referral benefits.'
        });
      }
    } catch (error) {
      console.error('Error validating referral:', error);
      this.setState({
        referralValid: false,
        validatingReferral: false,
        error: 'Could not validate referral code. You can still sign up.'
      });
    }
  };

  initializeAutocomplete = () => {
    if (!window.google) {
      console.error('Google Maps API not loaded');
      return;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(
      this.autocompleteInput.current,
      {
        types: ['establishment'],
        componentRestrictions: { country: 'us' },
        // All Basic Data fields — no Contact/Atmosphere surcharge.
        fields: ['name', 'formatted_address', 'address_components', 'place_id', 'geometry.location']
      }
    );

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      this.handlePlaceSelect(place);
    });

    this.setState({ autocomplete });
  };

  handlePlaceSelect = (place) => {
    if (!place.address_components) {
      return;
    }

    // Extract address components
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zipCode = '';

    place.address_components.forEach(component => {
      const types = component.types;

      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('locality')) {
        city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.short_name;
      }
      if (types.includes('postal_code')) {
        zipCode = component.long_name;
      }
    });

    const addressLine1 = `${streetNumber} ${route}`.trim();

    // geometry.location exposes lat/lng as functions, not properties.
    const location = place.geometry?.location;

    this.setState({
      businessName: place.name || this.state.businessName,
      addressLine1: addressLine1,
      city: city,
      state: state,
      zipCode: zipCode,
      placeDetails: place,
      placeId: place.place_id || null,
      latitude: location ? location.lat() : null,
      longitude: location ? location.lng() : null
    });
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    // Email becomes the Cognito username — normalize on entry so a mobile
    // keyboard's auto-capitalization can't create an unloggable account.
    this.setState({ [name]: name === 'contactEmail' ? value.trim().toLowerCase() : value });
  };

  validateForm = () => {
    const { businessName, contactName, contactEmail, contactPhone, addressLine1, city, state, zipCode, attributionSource, assistTermsAccepted } = this.state;

    if (!businessName || !contactName || !contactEmail || !contactPhone) {
      this.setState({ error: 'Please fill in all required fields.' });
      return false;
    }

    if (!addressLine1 || !city || !state || !zipCode) {
      this.setState({ error: 'Please select a valid business address using the autocomplete.' });
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactEmail)) {
      this.setState({ error: 'Please enter a valid email address.' });
      return false;
    }

    // Validate phone format (basic)
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    if (!phoneRegex.test(contactPhone)) {
      this.setState({ error: 'Please enter a valid 10-digit phone number.' });
      return false;
    }

    if (attributionSource === 'assist' && !assistTermsAccepted) {
      this.setState({ error: 'You must agree to the Jobvious Assist Program Agreement to continue.' });
      return false;
    }

    return true;
  };

  handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    this.setState({ error: null });

    // Validate form
    if (!this.validateForm()) {
      return;
    }

    this.setState({ loading: true });

    const signupData = {
      businessName: this.state.businessName,
      contactName: this.state.contactName,
      contactEmail: this.state.contactEmail,
      contactPhone: this.state.contactPhone,
      addressLine1: this.state.addressLine1,
      addressLine2: this.state.addressLine2,
      city: this.state.city,
      state: this.state.state,
      zipCode: this.state.zipCode,
      attributionSource: this.state.attributionSource,
      referringInstallerId: this.state.referringInstallerId,
      referralCode: this.state.referralCode,
      installerCategories: this.state.installerCategories,
      assetType: this.state.assetType,
      assistTermsAccepted: this.state.assistTermsAccepted,
      assistTermsVersion: CURRENT_ASSIST_TERMS_VERSION,
      placeId: this.state.placeId,
      latitude: this.state.latitude,
      longitude: this.state.longitude
    };

    try {
      const result = await DealerSignupService.submitDealerSignup(signupData);

      if (result.success) {
        this.setState({
          loading: false,
          submitted: true,
          error: null,
          successData: result
        });
      } else {
        this.setState({
          loading: false,
          error: result.message || 'An error occurred during signup. Please try again.',
          errorCode: result.error,
          errorAction: result.action
        });
      }
    } catch (error) {
      console.error('Signup error:', error);

      const errorData = error.response?.data || {};
      const statusCode = error.response?.status;

      // Handle specific error cases
      if (statusCode === 409) {
        // User already exists
        this.setState({
          loading: false,
          error: errorData.message || 'An account with this email already exists.',
          errorCode: 'USER_ALREADY_EXISTS',
          errorAction: 'LOGIN_AND_RESET_PASSWORD'
        });
      } else if (statusCode === 400) {
        // Validation error
        this.setState({
          loading: false,
          error: errorData.message || 'Please check your form fields and try again.',
          errorCode: 'VALIDATION_ERROR'
        });
      } else {
        // Generic error
        this.setState({
          loading: false,
          error: errorData.message || 'An error occurred during signup. Please try again.',
          errorCode: errorData.error || 'UNKNOWN_ERROR'
        });
      }
    }
  };

  renderReferralBanner = () => {
    const { validatingReferral, referralValid, referrerName, attributionSource } = this.state;

    if (attributionSource === 'assist') {
      return (
        <Alert
          variant="warning"
          className="text-center"
          style={{ maxWidth: '100%', borderLeft: '4px solid #FF9516' }}
        >
          <h5 className="mb-2">
            <i className="fa-sharp fa-solid fa-handshake me-2" />
            Sign up for Jobvious Assist
          </h5>
          <p className="mb-0">
            Submit customer referrals to qualified automotive specialists in your area,
            and earn commission when jobs complete.
          </p>
        </Alert>
      );
    }

    if (attributionSource !== 'installer') {
      return null;
    }

    if (validatingReferral) {
      return (
        <Alert variant="info">
          <Spinner animation="border" size="sm" /> Validating referral code...
        </Alert>
      );
    }

    if (referralValid) {
      return (
        <Alert variant="success" className="text-center" style={{ maxWidth: '100%' }}>
          <div style={{ lineHeight: '1.6' }}>
            <div className="mb-2">
              Thank you for joining Jobvious to make working with
            </div>
            <div className="mb-2">
              <strong style={{ fontSize: '1.2em' }}>{referrerName}</strong>
            </div>
            <div>
              as seamless as possible!
            </div>
          </div>
        </Alert>
      );
    }

    return null;
  };

  renderErrorMessage = () => {
    const { error, errorCode } = this.state;

    if (!error) return null;

    // User already exists - special handling
    if (errorCode === 'USER_ALREADY_EXISTS') {
      return (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>Account Already Exists</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex gap-2">
            <a href="/" className="btn btn-primary btn-sm">
              Go to Login
            </a>
          </div>
          <p className="mt-3 mb-0 small">
            Need help? Contact <a href="mailto:support@jobvious.com">support@jobvious.com</a>
          </p>
        </Alert>
      );
    }

    // Validation error
    if (errorCode === 'VALIDATION_ERROR') {
      return (
        <Alert variant="danger" dismissible onClose={() => this.setState({ error: null, errorCode: null })}>
          <strong>Validation Error:</strong> {error}
        </Alert>
      );
    }

    // Other errors with specific error codes
    const errorGuidance = {
      STRIPE_FAILED: 'There was an issue setting up payment processing. Please contact support.',
      GEOCODING_FAILED: 'We couldn\'t validate your address. Please check and try again.',
      DEALER_CREATION_FAILED: 'There was a database error. Please contact support.',
      USER_CREATION_FAILED: 'There was an error creating your user account. Please contact support.',
      COGNITO_FAILED: 'There was an error setting up authentication. Please contact support.',
      UNKNOWN_ERROR: 'An unexpected error occurred. Please try again or contact support.'
    };

    return (
      <Alert variant="danger" dismissible onClose={() => this.setState({ error: null, errorCode: null })}>
        <strong>Error:</strong> {error}
        {errorCode && errorGuidance[errorCode] && (
          <p className="mt-2 mb-0 small">
            <em>{errorGuidance[errorCode]}</em>
          </p>
        )}
        <p className="mt-2 mb-0 small">
          Need help? Contact <a href="mailto:support@jobvious.com">support@jobvious.com</a>
        </p>
      </Alert>
    );
  };

  renderForm = () => {
    const { loading } = this.state;

    return (
      <Container className="dealer-signup-container mt-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <div className="signup-card p-4">
              <div className="text-center mb-4">
                <img src={Logo} alt="Jobvious" className="signup-logo mb-3" />
                <h2 className="mb-2">Dealer Sign Up</h2>
                <p className="text-muted">
                  Join Jobvious to streamline your automotive service needs
                </p>
              </div>

              {this.renderReferralBanner()}

              {this.renderErrorMessage()}

              <Form onSubmit={this.handleSubmit}>
                {/* Business Address Autocomplete */}
                <Form.Group className="mb-3">
                  <Form.Label>Business Address <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    ref={this.autocompleteInput}
                    type="text"
                    placeholder="Start typing your business address..."
                    disabled={loading}
                    required
                  />
                  <Form.Text className="text-muted">
                    Start typing and select your business from the dropdown
                  </Form.Text>
                </Form.Group>

                {/* Business Name (auto-populated) */}
                <Form.Group className="mb-3">
                  <Form.Label>Business Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="businessName"
                    value={this.state.businessName}
                    onChange={this.handleInputChange}
                    placeholder="Business name"
                    disabled={loading}
                    required
                  />
                </Form.Group>

                <div className="form-group">
                  <label>What does your dealership sell?</label>
                  <select
                    className="form-control"
                    name="assetType"
                    value={this.state.assetType}
                    onChange={this.handleInputChange}
                  >
                    <option value="auto">Vehicles</option>
                    <option value="marine">Boats</option>
                  </select>
                </div>

                {/* Address Line 1 (auto-populated, read-only) */}
                <Form.Group className="mb-3">
                  <Form.Label>Street Address <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="addressLine1"
                    value={this.state.addressLine1}
                    onChange={this.handleInputChange}
                    placeholder="Street address"
                    disabled={loading}
                    readOnly
                    required
                  />
                </Form.Group>

                {/* Address Line 2 (optional) */}
                <Form.Group className="mb-3">
                  <Form.Label>Suite/Unit (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="addressLine2"
                    value={this.state.addressLine2}
                    onChange={this.handleInputChange}
                    placeholder="Suite, unit, building, floor, etc."
                    disabled={loading}
                  />
                </Form.Group>

                {/* City, State, Zip (auto-populated) */}
                <Row>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>City <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={this.state.city}
                        onChange={this.handleInputChange}
                        disabled={loading}
                        readOnly
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>State <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="state"
                        value={this.state.state}
                        onChange={this.handleInputChange}
                        disabled={loading}
                        readOnly
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Zip Code <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="zipCode"
                        value={this.state.zipCode}
                        onChange={this.handleInputChange}
                        disabled={loading}
                        readOnly
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Contact Name */}
                <Form.Group className="mb-3">
                  <Form.Label>Contact Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="contactName"
                    value={this.state.contactName}
                    onChange={this.handleInputChange}
                    placeholder="Your full name"
                    disabled={loading}
                    required
                  />
                </Form.Group>

                {/* Contact Email */}
                <Form.Group className="mb-3">
                  <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="contactEmail"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={this.state.contactEmail}
                    onChange={this.handleInputChange}
                    placeholder="your@email.com"
                    disabled={loading}
                    required
                  />
                  <Form.Text className="text-muted">
                    You'll receive login credentials at this email
                  </Form.Text>
                </Form.Group>

                {/* Contact Phone */}
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    name="contactPhone"
                    value={this.state.contactPhone}
                    onChange={this.handleInputChange}
                    placeholder="(555) 123-4567"
                    disabled={loading}
                    required
                  />
                </Form.Group>

                {/* Assist Program Agreement (Assist signups only) */}
                {this.state.attributionSource === 'assist' && (
                  <Form.Group className="mb-3 mt-3">
                    <Form.Check
                      type="checkbox"
                      id="assist-terms-checkbox"
                      checked={this.state.assistTermsAccepted}
                      onChange={(e) => this.setState({
                        assistTermsAccepted: e.target.checked,
                        error: null
                      })}
                      disabled={loading}
                      label={
                        <>
                          I agree to the{' '}
                          <a href={ASSIST_AGREEMENT_URL} target="_blank" rel="noopener noreferrer">
                            Jobvious Assist Program Agreement
                          </a>
                          {' '}on behalf of this dealership.
                        </>
                      }
                    />
                  </Form.Group>
                )}

                {/* Submit Button */}
                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 mt-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Creating Your Account...
                    </>
                  ) : (
                    'Create Dealer Account'
                  )}
                </Button>

                {/* Assist microcopy (Assist signups only) */}
                {this.state.attributionSource === 'assist' && (
                  <div className="text-center mt-3">
                    <small className="text-muted">
                      After signup, you'll be taken to your bookmarkable referral page.
                      Save the URL and use it any time you want to submit a customer referral.
                    </small>
                  </div>
                )}

                {/* Login Link */}
                <div className="text-center mt-3">
                  <small className="text-muted">
                    Already have an account? <a href="/">Log in here</a>
                  </small>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    );
  };

  renderAssistSuccess = () => {
    const { successData, contactEmail, businessName } = this.state;
    const dealerId = successData?.dealerId;
    const referralUrl = dealerId
      ? `${window.location.origin}/public/referral?dealer_id=${dealerId}`
      : null;

    return (
      <Container className="dealer-signup-container mt-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div className="signup-card p-5 text-center">
              <img src={Logo} alt="Jobvious" className="signup-logo mb-4" />

              <h2 className="mb-3">Welcome to Jobvious Assist</h2>

              <p className="lead mb-4">
                {businessName ? <>{businessName}'s account is ready.</> : 'Your account is ready.'}
                {' '}Bookmark your referral page below — that's where you'll submit customer referrals.
              </p>

              {referralUrl && (
                <div className="d-flex flex-column align-items-center mb-4">
                  <a
                    href={referralUrl}
                    className="btn btn-lg mb-3"
                    style={{ backgroundColor: '#FF9516', borderColor: '#FF9516', color: 'white', minWidth: '260px' }}
                  >
                    <i className="fa-sharp fa-solid fa-arrow-right me-2" />
                    Open My Referral Page
                  </a>
                  <BookmarkThisPage url={referralUrl} />
                </div>
              )}

              <Alert variant="info" className="text-start">
                <small>
                  <strong>Account credentials:</strong> A welcome email was sent to{' '}
                  <strong>{contactEmail}</strong> with login credentials in case you ever
                  need to sign in. For day-to-day use, your referral page above is all you need.
                </small>
              </Alert>

              <p className="text-muted mt-4 mb-0">
                <small>
                  Questions? Contact <a href="mailto:support@jobvious.com">support@jobvious.com</a>
                </small>
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    );
  };

  renderSuccessMessage = () => {
    const { attributionSource, successData, contactEmail } = this.state;

    if (attributionSource === 'assist') {
      return this.renderAssistSuccess();
    }

    const existingDealer = successData?.existingDealer || false;
    const warnings = successData?.warnings || [];
    const cognitoUsername = successData?.cognitoUsername || contactEmail;

    return (
      <Container className="dealer-signup-container mt-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <div className="signup-card p-5 text-center">
              <img src={Logo} alt="Jobvious" className="signup-logo mb-4" />


              <h2 className="mb-3">Welcome to Jobvious!</h2>

              {existingDealer ? (
                <p className="lead mb-4">
                  We found an existing dealership at this location. Your user account has been added successfully.
                </p>
              ) : (
                <p className="lead mb-4">
                  Your dealer account has been created successfully.
                </p>
              )}

              {/* Show warnings if any */}
              {warnings.length > 0 && (
                <Alert variant="warning" className="text-start mb-4">
                  <Alert.Heading>Note</Alert.Heading>
                  {warnings.map((warning, index) => (
                    <p key={index} className="mb-1">{warning}</p>
                  ))}
                  <p className="mb-0 mt-2">
                    <small>
                      Our support team has been notified and will contact you shortly if needed.
                    </small>
                  </p>
                </Alert>
              )}

              <Alert variant="info" className="text-start">
                <strong>Next Steps:</strong>
                <ol className="mb-0 mt-2" style={{ textAlign: 'left' }}>
                  <li>Check your email at <strong>{cognitoUsername}</strong></li>
                  <li>You'll receive login credentials with a temporary password</li>
                  <li>Click the "Go to Login" button below</li>
                  <li>Log in with your email and temporary password</li>
                  <li>You'll be prompted to create a new password on first login</li>
                </ol>
              </Alert>

              <p className="text-muted mt-4">
                <small>
                  Didn't receive the email? Check your spam folder or contact <a href="mailto:support@jobvious.com">support@jobvious.com</a>
                </small>
              </p>

              <a href="/" className="btn btn-primary mt-3 btn-lg">
                Go to Login
              </a>
            </div>
          </Col>
        </Row>
      </Container>
    );
  };

  render() {
    const { submitted } = this.state;

    return submitted ? this.renderSuccessMessage() : this.renderForm();
  }
}

export default DealerSignup;
